import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { formatPayloadValue } from "../../../helpers/activityLogHelpers";
import ActivityLogTimelineView from "../../../components/ActivityLog/ActivityLogTimelineView";

const EnterpriseActivityLog = ({ entId, entName }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs = [], loading = false, totalRecords = 0 } = useSelector((state) => state?.activityLog) || {};

  useEffect(() => {
    if (entId) {
      dispatch(getActivityLog({ module: "ENTERPRISE", entityId: entId, page, limit }));
    }
  }, [dispatch, entId, page]);

  const getActionConfig = (action) => {
    switch (action) {
      case "ENTERPRISE_CREATED": return { label: "Add Enterprise", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_APPROVED": return { label: "Approve Enterprise", color: "#e6ffed", textColor: "#22c55e" };
      case "ENTERPRISE_REJECTED": return { label: "Reject Enterprise", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_PROFILE_UPDATED": return { label: "Profile Update", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_DEACTIVATED": return { label: "Deactivation", color: "#fff0f6", textColor: "#eb2f96" };
      case "ENTERPRISE_BLACKLISTED": return { label: "Blacklist", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_REACTIVATED": return { label: "Reactivation", color: "#e6ffed", textColor: "#22c55e" };
      case "BUS_ENT_ACTIVATED": return { label: "Business Activated", color: "#e6ffed", textColor: "#22c55e" };
      case "BUS_ENT_REJECTED": return { label: "Business Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "BUS_ENT_REOPENED": return { label: "Business Reopened", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_CREATED": return { label: "POC Created", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_UPDATED": return { label: "POC Updated", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_DELETED": return { label: "POC Deleted", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_POC_APPROVED": return { label: "POC Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "ENTERPRISE_POC_REJECTED": return { label: "POC Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "ENTERPRISE_POC_REOPENED": return { label: "POC Reopened", color: "#e6f7ff", textColor: "#1890ff" };
      case "ENTERPRISE_POC_MAIL_SENT": return { label: "POC Mail Sent", color: "#e6f7ff", textColor: "#1890ff" };
      default: return { label: action.replace(/_/g, " "), color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const targetName = entName || log.payload?.entName || "Enterprise";
    const pocName = log.payload?.name || log.payload?.pocName || "POC";
    const pocId = log.payload?.pocId ? ` (${log.payload.pocId})` : "";
    const pocDept = log.payload?.department ? ` · ${log.payload.department}` : "";
    
    if (log.action === "ENTERPRISE_CREATED") {
      return `<strong>${name}</strong> (${role}) <strong>REGISTERED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_DEACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>DEACTIVATED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_BLACKLISTED") {
      return `<strong>${name}</strong> (${role}) <strong>BLACKLISTED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_REACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>REACTIVATED</strong> enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_DELETED") {
      return `<strong>${name}</strong> (${role}) <strong>DELETED</strong> POC <strong>"${pocName}"</strong>${pocId}${pocDept} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_CREATED") {
      return `<strong>${name}</strong> (${role}) <strong>CREATED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_UPDATED") {
      return `<strong>${name}</strong> (${role}) <strong>UPDATED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    if (log.action === "ENTERPRISE_POC_REOPENED") {
      return `<strong>${name}</strong> (${role}) <strong>REOPENED</strong> POC <strong>"${pocName}"</strong>${pocId} for enterprise <strong>"${targetName}"</strong>`;
    }
    
    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on enterprise <strong>"${targetName}"</strong>`;
  };

  return (
    <ActivityLogTimelineView
      title="Enterprise Lifecycle History"
      totalRecords={totalRecords}
      loading={loading}
      logs={logs}
      page={page}
      setPage={setPage}
      limit={limit}
      getActionConfig={getActionConfig}
      formatDesc={formatDesc}
      filterPayloadEntries={(payload) =>
        payload && typeof payload === "object"
          ? Object.entries(payload).filter(
              ([key]) =>
                key !== "entityTypeId" &&
                key !== "entityType" &&
                key !== "businessTypeId" &&
                key !== "businessType"
            )
          : []
      }
      formatPayloadKey={(key) => key.replace(/([A-Z])/g, " $1").trim()}
      formatPayloadValue={formatPayloadValue}
    />
  );
};

export default EnterpriseActivityLog;
